///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2021
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React from "react";

import AutoComplete from "../form-controls/AutoComplete";
import OutlinedInput from "../form-controls/OutlinedInput";
import {
  mode,
  booleanTypes,
  freightClassTypes,
  rateReturnTypes,
  ltlAcessorialTypes,
  currencyTypes
} from "../../helpers/types";
import disableRFQStatus, {
  getClosedBidReopenBlockedTooltipContent
} from "../../helpers/disableRFQStatus";
import messages from "../../helpers/messages";
import Tooltip from "../Tooltip";

function LessthanTruckload({
  modeDetails,
  setModeDetails,
  validationData,
  removeError,
  role,
  summary
}) {
  const commonProps = field => ({
    name: field,
    value: modeDetails[field],
    onFocus: () => !validationData[field].isValid && removeError("ltl", field),
    error: !validationData[field].isValid,
    validationMessage: validationData[field].validationMessage
  });

  const handleNumberChange = ({ target }) =>
    !target.validity.patternMismatch &&
    setModeDetails(mode.ltl, target.name, target.value);

  const handleTrimText = ({ target }) => {
    target && target.value
      ? setModeDetails(mode.ltl, target.name, target.value.trim())
      : setModeDetails(mode.ltl, target.name, "");
  };

  const closedBidReopenTooltip = getClosedBidReopenBlockedTooltipContent(
    modeDetails,
    summary
  );
  const rfqStatusCell = (
    <div data-testid="rfq-status-container" className="grid__cell one-half">
      <AutoComplete
        isDisabled={disableRFQStatus(mode.ltl, modeDetails, role, summary)}
        label={messages.rfqStatus.defaultMessage}
        value={modeDetails.currentStatus}
        staticOptions={modeDetails.rfqStatusOptions}
        handleChange={value =>
          setModeDetails(mode.ltl, "currentStatus", value)
        }
      />
    </div>
  );

  return (
    <div className="lessthan-truckload section-grid">
      {closedBidReopenTooltip ? (
        <Tooltip content={closedBidReopenTooltip}>{rfqStatusCell}</Tooltip>
      ) : (
        rfqStatusCell
      )}
      <div className="grid__cell one-half">
        <OutlinedInput
          label={messages.numberOfLanes.defaultMessage}
          type="text"
          pattern="^[1-9]\d{0,5}$"
          onChange={handleNumberChange}
          {...commonProps("laneCount")}
        />
      </div>
      <div className="grid__cell one-half">
        <OutlinedInput
          label={messages.volume.defaultMessage}
          type="text"
          pattern="^[1-9]\d{0,8}$"
          onChange={handleNumberChange}
          {...commonProps("volume")}
        />
      </div>
      <div
        data-testid="freight-class-container"
        className="grid__cell one-half"
      >
        <AutoComplete
          label={messages.freightClass.defaultMessage}
          handleChange={value =>
            setModeDetails(mode.ltl, "ltlFreightClasses", value)
          }
          staticOptions={freightClassTypes}
          isMulti
          {...commonProps("ltlFreightClasses")}
        />
      </div>
      <div
        data-testid="pallets-stackable-container"
        className="grid__cell one-half"
      >
        <AutoComplete
          label={messages.palletsStackable.defaultMessage}
          handleChange={value =>
            setModeDetails(mode.ltl, "palletsStackable", value)
          }
          staticOptions={booleanTypes}
          {...commonProps("palletsStackable")}
        />
      </div>
      <div
        data-testid="additional-currencies-container"
        className="grid__cell one-half"
      >
        <AutoComplete
          label={messages.additionalCurrenciesOptional.defaultMessage}
          value={modeDetails.additionalCurrencies}
          isMulti
          handleChange={value =>
            setModeDetails(mode.ltl, "additionalCurrencies", value)
          }
          staticOptions={currencyTypes}
        />
      </div>
      <div className="grid__cell one-half">
        <OutlinedInput
          label={messages.incumbentCarriers.defaultMessage}
          maxLength="1000"
          onChange={event =>
            setModeDetails(mode.ltl, "ltlIncumbentCarriers", event.target.value)
          }
          onBlur={handleTrimText}
          {...commonProps("ltlIncumbentCarriers")}
          isMulti
          dynamicHeight
        />
      </div>
      <div className="grid__cell one-half">
        <OutlinedInput
          label={messages.preferredCarriersOptional.defaultMessage}
          maxLength="1000"
          onChange={event =>
            setModeDetails(mode.ltl, "ltlPreferredCarriers", event.target.value)
          }
          name="ltlPreferredCarriers"
          value={modeDetails.ltlPreferredCarriers}
          onBlur={handleTrimText}
          isMulti
          dynamicHeight
        />
      </div>
      <div data-testid="rate-return-container" className="grid__cell one-half">
        <AutoComplete
          label={messages.rateReturnFormat.defaultMessage}
          handleChange={value =>
            setModeDetails(mode.ltl, "ltlRateReturnFormat", value)
          }
          staticOptions={rateReturnTypes}
          {...commonProps("ltlRateReturnFormat")}
        />
      </div>
      <div
        data-testid="guaranteed-shipment-container"
        className="grid__cell one-half"
      >
        <AutoComplete
          label={messages.requiresGuaranteedShipments.defaultMessage}
          handleChange={value =>
            setModeDetails(mode.ltl, "requiresGuaranteedShipment", value)
          }
          staticOptions={booleanTypes}
          {...commonProps("requiresGuaranteedShipment")}
        />
      </div>
      <div data-testid="accessorials-container" className="grid__cell one-half">
        <AutoComplete
          label={messages.accessorials.defaultMessage}
          handleChange={value =>
            setModeDetails(mode.ltl, "ltlAccessorials", value)
          }
          staticOptions={ltlAcessorialTypes}
          isMulti
          {...commonProps("ltlAccessorials")}
        />
      </div>
      <div className="grid__cell one-half mb-24">
        <OutlinedInput
          name="notes"
          isMulti
          value={modeDetails.notes}
          label={messages.additionalInformationOptional.defaultMessage}
          maxLength="3000"
          onChange={event =>
            setModeDetails(mode.ltl, "notes", event.target.value)
          }
          onBlur={handleTrimText}
          dynamicHeight
        />
      </div>
    </div>
  );
}

export default LessthanTruckload;
